<template>
  <div class="microbiology">
    <div class="microbiology__subheader width-responsiveness">
      <h2>Parcial Microbiologia</h2>
    </div>
    <MicrobiologyFilter @search="handleSearch" />

    <MicrobiologyResults
      v-if="hasSearched"
      :results="results"
      :loading="loading"
    />
  </div>
</template>

<script>
import MicrobiologyFilter from './MicrobiologyFilter.vue'
import MicrobiologyResults from './MicrobiologyResults.vue'
import microbiologyApi from '@/api/microbiology'
import notify from '@/api/notification'
import moment from 'moment'

export default {
  name: 'Microbiology',
  components: {
    MicrobiologyFilter,
    MicrobiologyResults,
  },
  data() {
    return {
      results: [],
      loading: false,
      hasSearched: false,
    }
  },
  methods: {
    async handleSearch(filters) {
      this.loading = true
      this.hasSearched = true

      try {
        let results = []
        const response = await microbiologyApi.searchMicrobiologyResults(filters)
        results = this.transformApiResponse(response.data)
        this.results = results
      } catch (error) {
        console.error('Erro ao buscar resultados:', error)
        notify({
          title: 'ERRO:',
          message: 'Erro ao buscar resultados. Tente novamente.',
        })
        this.results = []
      } finally {
        this.loading = false
      }
    },

    transformApiResponse(apiData) {
      const orders = Array.isArray(apiData) ? apiData : apiData.content

      if (!Array.isArray(orders)) {
        return []
      }

      return orders
        .map(order => {
          if (!order.exams || !Array.isArray(order.exams)) {
            return []
          }

          return order.exams.map((exam, examIndex) => {
            return {
              id: `${order.orderId || ''}_${exam.examRequestId || exam.id || examIndex}`,
              orderId: order.orderId || '',
              orderIdLis: order.orderIdLis || order.orderLisId || exam.orderLisId || '',
              patient: order.patient || '',
              material: exam.material || '',
              exam: exam.name || '',
              examCode: exam.code || '',
              sampleId: exam.sampleId || '',
              requestDate: order.registrationDate
                ? moment(order.registrationDate).format('DD/MM/YYYY - HH:mm')
                : '',
              collectionDate: order.collectionDate
                ? moment(order.collectionDate).format('DD/MM/YYYY - HH:mm')
                : '',
              incubationTime: this.getIncubationTime(exam),
              status:
                typeof exam.finalized === 'boolean'
                  ? exam.finalized
                    ? 'Finalizado'
                    : 'Em andamento'
                  : '',
              sitio: '',
              progress: this.transformProgress(exam),
              viewed: !!exam.read,
            }
          })
        })
        .flat()
    },

    transformProgress(exam) {
      const progressSteps = []

      if (exam.enterDate) {
        progressSteps.push({
          date: moment(exam.enterDate).format('DD MMM'),
          time: moment(exam.enterDate).format('HH:mm'),
          year: moment(exam.enterDate).format('YYYY'),
          step: 'Início',
          description: '',
          status: 'completed',
        })
      }

      if (exam.timeline && Array.isArray(exam.timeline)) {
        exam.timeline.forEach((timelineItem, index) => {
          if (timelineItem.events && Array.isArray(timelineItem.events)) {
            const events = timelineItem.events.filter(
              event => event.eventDate && event.nameCritique !== 'Positividade em:'
            )

            if (events.length === 0) return

            const firstEvent = events[0]
            const eventMoment = moment(firstEvent.eventDate)
            const isPartial =
              timelineItem.status && timelineItem.status.toLowerCase().includes('parcial')
            const isFinal =
              timelineItem.status && timelineItem.status.toLowerCase().includes('final')
            const subDescriptions = events.map(event => {
              const hasPartialAntibiogram = isPartial && this.hasPartialAntibiogram(event)

              return {
                text: event.result || '',
                additionalInfo: hasPartialAntibiogram ? 'Antibiograma parcial' : '',
                partialAntibiogram: hasPartialAntibiogram
                  ? this.buildPartialAntibiogram(event)
                  : null,
              }
            })

            progressSteps.push({
              date: eventMoment.format('DD MMM'),
              time: eventMoment.format('HH:mm'),
              year: eventMoment.format('YYYY'),
              step: timelineItem.status || `${index + 1}ª Etapa`,
              description: isPartial
                ? 'RESULTADO PARCIAL'
                : isFinal
                  ? 'RESULTADO FINAL'
                  : 'ETAPA',
              subDescriptions,
              status: isFinal ? 'final' : 'completed',
            })
          }
        })
      }

      return progressSteps
    },

    getIncubationTime(exam) {
      if (!Array.isArray(exam.timeline)) return ''

      for (const timelineItem of exam.timeline) {
        if (!Array.isArray(timelineItem.events)) continue

        const incubationEvent = timelineItem.events.find(
          event => event.nameCritique === 'Positividade em:'
        )

        if (incubationEvent) return incubationEvent.result || ''
      }

      return ''
    },

    hasPartialAntibiogram(event) {
      return !!(
        event &&
        (event.partialAntibiogram ||
          event.antibiogram ||
          event.antibiogramData ||
          event.partialResult)
      )
    },

    buildPartialAntibiogram(event) {
      const data =
        event.partialAntibiogram || event.antibiogram || event.antibiogramData || {}
      const rows = Array.isArray(data.rows)
        ? data.rows
        : Array.isArray(data.items)
          ? data.items.map(item => ({
              antibiogram: item.antibiogram || item.name || '',
              result: item.result || '',
              cim: item.cim || item.cimValue || '',
            }))
          : []

      return {
        microorganism: data.microorganism || '',
        reportDate: data.reportDate || '',
        unit: data.unit || '',
        rows,
        footerNote: data.footerNote || '',
        notes: Array.isArray(data.notes) ? data.notes : [],
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.microbiology {
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: center;
  margin-top: 100px;
  font-family: 'Exo 2', sans-serif !important;
  padding-bottom: 40px;

  &__subheader {
    display: flex;
    justify-content: space-between;
    align-content: center;
    z-index: 1;

    h2 {
      font-size: 22px;
      font-weight: 600;
      letter-spacing: -0.3px;
      text-align: left;
      color: #7a7a7a;
      line-height: 30px;
    }
  }
}
</style>
